import type { CommunityProject, QuestKind } from "./types.js";

export const PROJECT_EFFORT: Record<QuestKind, number> = {
  main: 10,
  bonus: 5,
  helpful: 2,
  lfg: 10,
  event: 10
};

export function defaultProjectGoal(activeContributors: number): number {
  return Math.min(5_000, Math.max(800, Math.round(activeContributors) * 160));
}

export function projectRatio(project: CommunityProject): number {
  return Math.min(1, project.progress / project.goal);
}

export function projectStageIndex(project: CommunityProject): number {
  const ratio = projectRatio(project);
  let activeIndex = 0;
  project.stages.forEach((stage, index) => {
    if (ratio >= stage.threshold) activeIndex = index;
  });
  return activeIndex;
}

export function addProjectEffort(project: CommunityProject, effort: number): CommunityProject {
  if (!Number.isSafeInteger(effort) || effort < 0) throw new Error("Project effort must be nonnegative");
  const progress = Math.min(project.goal, project.progress + effort);
  return { ...project, progress, status: progress >= project.goal ? "complete" : project.status };
}

export interface PollChoice {
  blueprintId: string;
  name: string;
}

/**
 * Which blueprint a closed poll chose.
 *
 * Ties break on the order the choices were authored rather than on anything the database happens
 * to return first, so closing the same poll twice cannot pick two different winners. A poll with
 * no votes at all has no winner — queueing the first option because nobody spoke would put a
 * project in front of the village that nobody asked for.
 */
export function tallyPollWinner(choices: PollChoice[], votes: Array<{ blueprintId: string; votes: number }>): PollChoice | null {
  const counts = new Map(votes.map((entry) => [entry.blueprintId, entry.votes]));
  let winner: PollChoice | null = null;
  let best = 0;
  for (const choice of choices) {
    const count = counts.get(choice.blueprintId) ?? 0;
    if (count > best) {
      best = count;
      winner = choice;
    }
  }
  return winner;
}

export const TOWN_SQUARE_PROJECT: CommunityProject = {
  id: "project-town-square",
  name: "Town Square",
  description: "A welcoming center with a fountain, notice board, benches, and room for future gatherings.",
  goal: 800,
  progress: 284,
  status: "active",
  queuePosition: 0,
  contributors: 7,
  stages: [
    { threshold: 0, name: "Survey", description: "Markers and material piles outline the square." },
    { threshold: 0.25, name: "Foundation", description: "Stonework and curved approach paths are taking shape." },
    { threshold: 0.5, name: "Gathering place", description: "The fountain, notice board, and benches arrive." },
    { threshold: 0.75, name: "Finishing touches", description: "Trees, lamps, banners, and vendor spaces fill the square." },
    { threshold: 1, name: "Open", description: "The Town Square is ready for the whole Tribe." }
  ]
};
